import { Injectable } from '@angular/core';
import { ShareService } from '@core/services/share-data/share-data.service';
import {
	BlindConfigSetupItem,
	BlindConfigurationStates,
	BlindConfigurationStatuses,
	BlindType,
	SectionKey,
	SectionKeysEnum,
} from '@root/app.interfaces';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { STORAGE_NAMES } from '@root/app.config';
import { VariantService } from '@core/services/variant/variant.service';

@Injectable({
	providedIn: 'root',
})
export class BlindStateService {
	private readonly REQUIRED: SectionKey[] = [
		SectionKeysEnum.Material,
		SectionKeysEnum.Size,
		SectionKeysEnum.Operation,
		SectionKeysEnum.FrameColor,
		SectionKeysEnum.TopStyle,
		SectionKeysEnum.BottomBar,
		SectionKeysEnum.Mounting,
	];
	private blindStates: BlindConfigurationStates = {};
	private blindHasHadStates: BlindConfigurationStates = {};
	private blindOrder: number[] = []; // preserves blind order

	constructor(
		private shareService: ShareService,
		private sessionStorageService: SessionStorageService,
		private variant: VariantService,
	) {}

	isBlindConfigured(blindId: number): boolean {
		const set = this.blindStates[blindId];
		return !!set && this.REQUIRED.every((section) => set.has(section));
	}

	allBlindsConfigured(): boolean {
		const sets = Object.values(this.blindStates);
		if (sets.length === 0) {
			return false;
		}
		return sets.every((set) => set && this.REQUIRED.every((k) => set.has(k)));
	}

	getBlindsConfiguredStatuses(): BlindConfigurationStatuses {
		const sets = Object.entries(this.blindStates).map(([id, set]) => [id, set && this.REQUIRED.every((k) => set.has(k))]);

		return Object.fromEntries(sets);
	}

	missingSections(blindId: number): SectionKey[] {
		const set = this.blindStates[blindId];
		return set ? this.REQUIRED.filter((k) => !set.has(k)) : [...this.REQUIRED];
	}

	nextMissingSection(): { blindId: number; section: SectionKey } | null {
		for (const id of this.blindOrder) {
			const missing = this.missingSections(id);
			if (missing.length > 0) {
				return { blindId: id, section: missing[0] };
			}
		}

		return null;
	}

	// Sections that don't need scroll into view when opened
	getSectionWithoutScroll(blindType: BlindType): SectionKey[] {
		if (!this.variant.isShop()) {
			return [];
		}

		const blindSectionConfigs = this.getSectionConfigs(blindType);

		if (!blindSectionConfigs) {
			return [];
		}

		return this.REQUIRED.filter((sectionKey, i, arr) => {
			if (i === 0) {
				return true;
			}

			const prevKey = arr[i - 1];
			return blindSectionConfigs[prevKey]?.some((item) => item.optional?.length > 0);
		});
	}

	getSectionConfigs(
		blindType: BlindType,
	): Record<Exclude<SectionKey, SectionKeysEnum.ColorOfFixtures>, (BlindConfigSetupItem & { optional?: [] })[]> {
		const getBlindConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config);

		if (!Object.hasOwn(getBlindConfig, blindType)) {
			return null;
		}

		if (blindType === 'outdoor') {
			const outdoorConfig = getBlindConfig['outdoor'];

			return {
				[SectionKeysEnum.Material]: outdoorConfig.material,
				[SectionKeysEnum.Size]: null,
				[SectionKeysEnum.Operation]: outdoorConfig.frame.operation,
				[SectionKeysEnum.FrameColor]: outdoorConfig.frame.frame_color,
				[SectionKeysEnum.TopStyle]: outdoorConfig.frame.top_style,
				[SectionKeysEnum.BottomBar]: outdoorConfig.frame.bottom_bar_protection,
				[SectionKeysEnum.Mounting]: outdoorConfig.frame.mounting,
			};
		}

		// TODO: Complete for interior blinds when needed
	}

	openNextSection(blindId: number): void {
		const sameBlindMissingSections = this.missingSections(blindId);

		if (sameBlindMissingSections.length > 0) {
			this.shareService.setOpenSection({ blindId, section: sameBlindMissingSections[0] });
		} else {
			const next = this.nextMissingSection();
			if (next) {
				this.shareService.setOpenSection(next);
			}
		}
	}

	private shouldAutoOpenNextSection(blindId: number, section: SectionKeysEnum, openNextSection: boolean | null): boolean {
		const isShopMode = this.variant.isShop();
		const isNextSectionAllowed = openNextSection || openNextSection === null;

		// To prevent opening the Blind from the List when changing the material for the previous Blind.
		const isConfigured = this.isBlindConfigured(blindId);
		const isMaterialSectionChanged = isConfigured && section === SectionKeysEnum.Material;

		return isShopMode && isNextSectionAllowed && !isMaterialSectionChanged;
	}

	closeNextSection(blindId): void {
		const sameBlindMissingSections = this.missingSections(blindId);

		if (sameBlindMissingSections.length > 1) {
			this.shareService.setCloseSection({ blindId, section: sameBlindMissingSections[1] });
		}
	}

	checkBlindsConfigured(): void {
		this.shareService.setBlindsConfigurationStatuses(this.getBlindsConfiguredStatuses());

		const allConfigured = this.allBlindsConfigured();
		if (this.shareService.getBlindsConfiguredValue() !== allConfigured) {
			this.shareService.setBlindsConfigured(allConfigured);
		}
	}

	markConfigured(blindId: number, section: SectionKey, openNextSection: boolean = null): void {
		if (!this.blindStates[blindId]) {
			this.blindStates[blindId] = new Set<SectionKey>();
		} else if (this.blindStates[blindId].has(section)) {
			// In the case of size, the section is already technically configured however we don't want to open the next section until the user is finished
			if (openNextSection && (!this.blindHasHadStates[blindId].has(section) || this.missingSections(blindId).length > 0)) {
				// Only want to open the next section if size hasn't been configure before or there is a section on the current blind that hasn't been configured yet
				this.openNextSection(blindId);
				this.blindHasHadStates[blindId].add(section);
			}

			return;
		}

		this.blindStates[blindId].add(section);
		this.checkBlindsConfigured();

		if (this.shouldAutoOpenNextSection(blindId, section, openNextSection)) {
			this.openNextSection(blindId);
		}
	}

	markUnconfigured(blindId: number, section: SectionKey): void {
		if (this.blindStates[blindId] && this.blindStates[blindId].has(section)) {
			this.blindStates[blindId].delete(section);

			this.checkBlindsConfigured();
			this.closeNextSection(blindId);
		}
	}

	blindCreated(blindId: number, duplicateId: number = null): void {
		if (duplicateId && this.blindStates[duplicateId]) {
			this.blindStates[blindId] = new Set<SectionKey>(this.blindStates[duplicateId]);
			this.blindHasHadStates[blindId] = new Set<SectionKey>(this.blindHasHadStates[duplicateId]);
		} else {
			if (!this.blindStates[blindId]) {
				this.blindStates[blindId] = new Set<SectionKey>();
				this.blindHasHadStates[blindId] = new Set<SectionKey>();
			}
		}

		if (!this.blindOrder.includes(blindId)) {
			this.blindOrder.push(blindId);
		}

		this.checkBlindsConfigured();
	}

	blindDeleted(blindId: number): void {
		delete this.blindStates[blindId];
		delete this.blindHasHadStates[blindId];
		this.blindOrder = this.blindOrder.filter((id) => id !== blindId);

		this.checkBlindsConfigured();
	}

	deleteAll(): void {
		this.blindStates = {};
		this.blindHasHadStates = {};
		this.blindOrder = [];
		this.checkBlindsConfigured();
	}

	private isSectionKey = (value: string): value is SectionKey => {
		return (Object.values(SectionKeysEnum) as string[]).includes(value);
	};

	reconcileFromStorage(): void {
		this.deleteAll();
		const storedBlinds = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		storedBlinds.forEach((blind) => {
			this.blindCreated(blind.blind_id);
			Object.entries(blind.setup ?? {}).forEach(([key, section]) => {
				if (section && key === 'frames' && typeof section === 'object') {
					Object.entries(section as Record<string, { configured?: boolean }>).forEach(([frameKey, frameSection]) => {
						const normalised = frameKey.replace(/_/g, '-'); // e.g. frame_color -> frame-color
						if (this.isSectionKey(normalised) && frameSection?.configured) {
							this.blindStates[blind.blind_id].add(normalised);
						}
					});
				} else if (section && typeof section === 'object' && 'configured' in section) {
					const normalised = key.replace(/_/g, '-');
					if (this.isSectionKey(normalised) && section.configured) {
						this.blindStates[blind.blind_id].add(normalised);
					}
				}
			});
		});

		this.checkBlindsConfigured();
		this.shareService.setBlindsConfigReady(true);
	}
}
